import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getScreenKey, getScreenLabel } from '../utils/routeBreadcrumbs';

const NavigationOriginContext = createContext(null);

/** @typedef {{ to: string, label: string }} NavOrigin */

/**
 * Tracks a visit stack so the header back button returns to the screen the user
 * actually came from. Minor header tabs (?tab=) do not push — only main-header
 * page changes (pathname / significant search) are remembered.
 *
 * Sidebar links should set `state: { navRoot: true }` so a previous building
 * does not stick in the title (e.g. "Building #25 Registered Company").
 */
export function NavigationOriginProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const visitStackRef = useRef(/** @type {{ key: string, to: string, label: string }[]} */ ([]));
  const [origin, setOrigin] = useState(/** @type {NavOrigin | null} */ (null));

  useEffect(() => {
    const { pathname, search, state } = location;
    const key = getScreenKey(pathname, search);
    const to = `${pathname || '/'}${search || ''}`;
    const label = getScreenLabel(pathname, search);
    const stack = visitStackRef.current;

    if (state?.navRoot) {
      visitStackRef.current = [{ key, to, label }];
      setOrigin(null);
      return;
    }

    if (state?.fromBack) {
      if (stack.length > 0) stack.pop();
      const prev = stack.length >= 2 ? stack[stack.length - 2] : null;
      setOrigin(prev ? { to: prev.to, label: prev.label } : null);
      return;
    }

    const explicit = state?.origin;
    if (explicit && typeof explicit.to === 'string' && explicit.label) {
      const top = stack[stack.length - 1];
      if (!top || top.key !== key) {
        stack.push({ key, to, label });
      } else {
        top.to = to;
        top.label = label;
      }
      setOrigin({ to: explicit.to, label: explicit.label });
      return;
    }

    const top = stack[stack.length - 1];
    if (top?.key === key) {
      top.to = to;
      top.label = label;
      const prev = stack.length >= 2 ? stack[stack.length - 2] : null;
      setOrigin(prev ? { to: prev.to, label: prev.label } : null);
      return;
    }

    stack.push({ key, to, label });
    const prev = stack.length >= 2 ? stack[stack.length - 2] : null;
    setOrigin(prev ? { to: prev.to, label: prev.label } : null);
  }, [location]);

  const navigateBack = useMemo(
    () => (/** @type {NavOrigin} */ target) => {
      if (!target?.to) return;
      navigate(target.to, { state: { fromBack: true } });
    },
    [navigate]
  );

  const value = useMemo(
    () => ({
      origin,
      navigateBack,
    }),
    [origin, navigateBack]
  );

  return (
    <NavigationOriginContext.Provider value={value}>{children}</NavigationOriginContext.Provider>
  );
}

export function useNavigationOrigin() {
  return useContext(NavigationOriginContext);
}
